'use client'

import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/platform/ui'
import { GRADE_COLORS } from '@/lib/radar/scoring'
import type { ActionItem, ToolKey, ToolState } from '@/types/radar'
import { GradeScale } from './GradeScale'
import { SignalProfile } from './SignalProfile'
import { TrendSparkline } from './TrendSparkline'
import type { TrendPoint } from './useTrendHistory'
import { Chip, InstrumentLabel, Panel } from './ui'
import { DeltaBadge } from './ScoreHero'

/**
 * The dashboard headline in the radar-v2 arrangement: score, signal profile
 * and next move side by side, with the trend as a full-width strip beneath.
 *
 * A dashboard-only FORK of `ScoreHero`, not a variant of it. `ScoreHero` is
 * imported by `RadarDemo` and `HomeHero`, so giving it a three-column layout
 * would change the marketing site. The two are expected to drift; what they
 * must NOT drift on is the withheld-grade language, which is the product's
 * position on refusing to score — that copy is duplicated verbatim below and
 * both copies move together.
 *
 * The trend keeps its place. The radar-v2 hero has no trend at all, but the
 * hero sparkline is the one surface a trend is read on (the History chart was
 * deleted for being the same series twice), so dropping it here would remove
 * the feature rather than rearrange it.
 */
/**
 * Effort is the ONLY dimension of an action surfaced here.
 *
 * The radar-v2 mock puts an "Est. lift +4 pts" figure beside the next move.
 * Nothing in the audit produces a per-action point projection, so that number
 * would be invented — it is omitted rather than approximated. `impact` is
 * likewise withheld: it is a hand-authored severity on the action, not a
 * measurement, and a headline slot would read it as evidence.
 */
const EFFORT_LABEL: Record<ActionItem['effort'], string> = {
  low: 'Quick win',
  medium: 'Medium effort',
  high: 'Deep work',
}

/**
 * Effort chips render as 11px TEXT (see `Chip`), so every value here has to
 * clear 4.5:1 on the dashboard's own surfaces in both themes — not merely
 * read as the right hue. `--growth` is a FILL token and was 2.79:1 on the
 * light card; `--accent-green-text` is the contrast-tuned text green that
 * already exists for exactly this (4.90:1 light / 9.44:1 dark). The other
 * two are text tokens and now pass at their own values (F-05).
 */
const EFFORT_COLOR: Record<ActionItem['effort'], string> = {
  low: 'var(--accent-green-text)',
  medium: 'var(--warning-text)',
  high: 'var(--pm-text-muted)',
}

export function DashboardHero({
  score,
  grade,
  scoredCount,
  withheldCount,
  interpretation,
  previousScore,
  activeDomain,
  tools,
  nextAction,
  onReviewAction,
  isRunning = false,
  trend,
}: {
  score: number | null
  grade: string
  scoredCount: number
  withheldCount: number
  interpretation: { headline: string; description: string } | null
  previousScore?: number | null
  activeDomain?: string | null
  tools: Partial<Record<ToolKey, ToolState>>
  /** Top-ranked action, already in canonical order. */
  nextAction?: ActionItem | null
  onReviewAction?: () => void
  /**
   * ONE flag for "the audit is still in flight", read by every panel here.
   * The dashboard renders for real once three tools have data (page.tsx), so
   * these panels are mounted mid-measurement and must not state conclusions
   * yet. It was previously a `showTrend` flag that only the trend honoured,
   * which is how "No open recommendations from this run" came to render
   * during a running scan.
   */
  isRunning?: boolean
  trend?: TrendPoint[]
}) {
  const hasScore = score !== null
  const gradeColor = hasScore
    ? (GRADE_COLORS[grade] ?? 'var(--muted-foreground)')
    : 'var(--muted-foreground)'

  const delta =
    hasScore && previousScore !== null && previousScore !== undefined
      ? score - previousScore
      : null

  return (
    <Panel gradientRule>
      {/* Three across only at xl. The track minimums total 840px, and the
          content column is viewport - 232px rail - 64px gutters, so 3-up needs
          a 1136px viewport: at lg (1024px) only 728px is available and the row
          overflowed by 112px. lg gets 2-up with the callout spanning. */}
      <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(280px,0.86fr)_minmax(300px,1fr)_minmax(260px,0.84fr)]'>
        {/* ── Readiness score ───────────────────────────────────────── */}
        <div className='flex flex-col gap-5 border-b border-hairline px-6 py-7 sm:px-8 lg:border-r xl:border-b-0'>
          <InstrumentLabel tone='secondary'>AI readiness score</InstrumentLabel>

          {hasScore ? (
            <div className='flex flex-wrap items-baseline gap-x-5 gap-y-2'>
              <span
                className='font-display text-[64px] font-bold leading-none tracking-[-0.03em] sm:text-[80px]'
                style={{ color: gradeColor }}
              >
                {grade}
              </span>
              <span className='font-display text-[28px] font-medium tracking-[-0.02em]'>
                {score}
                <span className='text-instrument text-[22px]'>/100</span>
              </span>
              {delta !== null && <DeltaBadge delta={delta} />}
            </div>
          ) : (
            <div className='flex flex-col gap-2'>
              <span className='font-display text-[40px] font-medium leading-none tracking-[-0.02em] text-muted-foreground'>
                Not scored
              </span>
              <span className='block text-sm text-muted-foreground'>
                {withheldCount > 0
                  ? `Every completed check declined to grade${
                      activeDomain ? ` ${activeDomain}` : ''
                    }. A withheld grade is a refusal to score, not a zero.`
                  : 'No gradeable results yet.'}
              </span>
            </div>
          )}

          <GradeScale score={score} />

          {interpretation && (
            <div className='flex flex-col gap-1.5'>
              <span className='block text-base font-semibold'>
                {interpretation.headline}
              </span>
              <span className='block text-pretty text-sm leading-relaxed text-muted-foreground'>
                {interpretation.description}
              </span>
            </div>
          )}

          <span className='block text-xs text-instrument'>
            {hasScore
              ? `Mean across ${scoredCount} gradeable ${scoredCount === 1 ? 'tool' : 'tools'}`
              : 'No gradeable tools in this run'}
            {withheldCount > 0 &&
              ` · ${withheldCount} withheld ${withheldCount === 1 ? 'grade' : 'grades'}`}
          </span>
        </div>

        {/* ── Signal profile ────────────────────────────────────────── */}
        <div className='border-b border-hairline xl:border-b-0 xl:border-r'>
          <SignalProfile
            tools={tools}
            overallScore={score}
            isRunning={isRunning}
          />
        </div>

        {/* ── Next best move ────────────────────────────────────────── */}
        <div className='flex flex-col gap-4 px-6 py-7 sm:px-8 lg:col-span-2 xl:col-span-1'>
          <InstrumentLabel tone='secondary'>Next best move</InstrumentLabel>

          {nextAction ? (
            <>
              <span className='block text-pretty text-base font-semibold leading-snug'>
                {nextAction.title}
              </span>
              <span className='block max-w-[42ch] text-pretty text-sm leading-relaxed text-muted-foreground'>
                {nextAction.why}
              </span>
              <div className='flex flex-col gap-3'>
                <div className='flex flex-wrap gap-2'>
                  <Chip color={EFFORT_COLOR[nextAction.effort]}>
                    {EFFORT_LABEL[nextAction.effort]}
                  </Chip>
                  <Chip color='var(--pm-text-muted)'>
                    {nextAction.category.replace(/-/g, ' ')}
                  </Chip>
                </div>
                {/* Same magenta fill as "Run audit" — this is the one thing
                    the panel is asking you to do. `variant='cta'` resolves to
                    --cta-fill, the AA token, NOT --cta: a white label on the
                    raw brand pink misses 4.5:1. */}
                {onReviewAction && (
                  <Button
                    type='button'
                    variant='cta'
                    trailingIcon={ArrowRight}
                    onClick={onReviewAction}
                    className='mt-3 self-start'
                  >
                    Review recommendation
                  </Button>
                )}
              </div>
            </>
          ) : (
            <span className='block text-sm text-muted-foreground'>
              {isRunning
                ? 'Recommendations appear when the audit finishes.'
                : 'No open recommendations from this run.'}
            </span>
          )}
        </div>
      </div>

      {/* ── Trend ─────────────────────────────────────────────────────── */}
      <div className='border-t border-hairline'>
        {!isRunning ? (
          // Wider viewBox than ScoreHero's: this strip spans the full panel
          // rather than a 440px column, and the 560-wide box would render a
          // 330px-tall chart at that width.
          <TrendSparkline domain={activeDomain} data={trend} viewWidth={980} />
        ) : (
          <div className='px-6 py-5 sm:px-8'>
            <span className='text-xs text-instrument'>
              Trend updates when the audit finishes.
            </span>
          </div>
        )}
      </div>
    </Panel>
  )
}
